// src/app/pages/admin/AdminUserBalancesPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Users, Wallet, RefreshCcw, X, ArrowUpDown } from "lucide-react";

import {
  adminSearchUsers,
  adminGetUserBalances,
  adminAdjustBalance,
} from "../../services/adminWallet.js";

function Pill({ children }) {
  return (
    <span className="px-2 py-1 rounded-md text-xs bg-gray-800/80 border border-gray-700 text-gray-200">
      {children}
    </span>
  );
}

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl rounded-2xl bg-gray-950 border border-gray-800 shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <div className="text-white font-semibold">{title}</div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-900 border border-gray-800 text-gray-300 hover:text-white"
            type="button"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const CORE_PAIRS = [
  { asset: "BTC", network: "MAINNET", label: "BTC • MAINNET" },
  { asset: "ETH", network: "MAINNET", label: "ETH • MAINNET" },
  { asset: "USDT", network: "TRON", label: "USDT • TRON" },
];

function normalize(s) {
  return String(s || "").trim();
}

function formatNum(n, d = 2) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "0";
  return x.toLocaleString(undefined, {
    maximumFractionDigits: d,
    minimumFractionDigits: d,
  });
}

export function AdminUserBalancesPage() {
  const qc = useQueryClient();

  // --- Search state
  const [q, setQ] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // --- Adjust modal state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [pair, setPair] = useState(CORE_PAIRS[2]); // default USDT/TRON

  const [deltaAvail, setDeltaAvail] = useState("");
  const [deltaLocked, setDeltaLocked] = useState("");
  const [reason, setReason] = useState("");

  // --- Search users (server-side)
  const {
    data: usersResp,
    isLoading: usersLoading,
    error: usersErrRaw,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["adminUserSearch", q],
    queryFn: () => adminSearchUsers({ q, limit: 25, offset: 0 }),
    staleTime: 5_000,
  });

  const users = useMemo(() => (usersResp?.items ? usersResp.items : []), [usersResp]);

  const usersErr =
    usersErrRaw?.response?.data?.detail || usersErrRaw?.message || "";

  // --- Get balances for selected user
  const selectedUserId = selectedUser?.id || null;

  const {
    data: balancesResp,
    isLoading: balancesLoading,
    error: balancesErrRaw,
    refetch: refetchBalances,
  } = useQuery({
    queryKey: ["adminUserBalances", selectedUserId],
    queryFn: () =>
      adminGetUserBalances({
        user_id: selectedUserId,
        limit: 100,
        offset: 0,
      }),
    enabled: !!selectedUserId,
    staleTime: 2_000,
  });

  const balances = useMemo(() => {
    const raw = Array.isArray(balancesResp?.items) ? balancesResp.items : [];
    // Map to core pairs (ensure always present even if missing rows)
    const map = new Map();
    for (const b of raw) {
      map.set(`${b.asset}|${b.network}`, b);
    }
    return CORE_PAIRS.map((p) => {
      const k = `${p.asset}|${p.network}`;
      const row = map.get(k);
      return {
        ...p,
        available: row?.available ?? "0",
        locked: row?.locked ?? "0",
      };
    });
  }, [balancesResp]);

  const balancesErr =
    balancesErrRaw?.response?.data?.detail || balancesErrRaw?.message || "";

  // --- Adjust balance mutation
  const adjustMut = useMutation({
    mutationFn: adminAdjustBalance,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["adminUserBalances", selectedUserId] });
      // user wallet page will also need to refetch when user opens it;
      // if you have admin impersonation/testing you can invalidate walletOverview too.
      setAdjustOpen(false);
      setDeltaAvail("");
      setDeltaLocked("");
      setReason("");
      await refetchBalances();
    },
  });

  const adjustErr =
    adjustMut.error?.response?.data?.detail ||
    adjustMut.error?.message ||
    "";

  // Keep selected user if search results still contain them; otherwise keep selection as-is
  useEffect(() => {
    if (!selectedUserId) return;
    // nothing required; selection remains stable
  }, [selectedUserId, users]);

  const openAdjust = (p) => {
    if (!selectedUserId) return;
    setPair(p);
    setAdjustOpen(true);
  };

  const submitAdjust = (e) => {
    e.preventDefault();
    if (!selectedUserId) return;

    const da = Number(deltaAvail || 0);
    const dl = Number(deltaLocked || 0);
    const r = normalize(reason);

    if (!r || r.length < 3) {
      alert("Reason must be at least 3 characters.");
      return;
    }
    if (!Number.isFinite(da) || !Number.isFinite(dl)) {
      alert("Delta values must be valid numbers.");
      return;
    }
    if (da === 0 && dl === 0) {
      alert("Provide at least one delta (available or locked).");
      return;
    }

    adjustMut.mutate({
      user_id: selectedUserId,
      asset: pair.asset,
      network: pair.network,
      delta_available: da,
      delta_locked: dl,
      reason: r,
    });
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-800 grid place-items-center text-white">
              <Users size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">User Balances</h1>
              <p className="text-sm text-gray-400">
                Search users and adjust ledger balances (does not move DFNS treasury).
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => {
            refetchUsers();
            if (selectedUserId) refetchBalances();
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
          type="button"
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="mt-6 bg-gray-900 border border-gray-800 rounded-2xl p-5">
        <div className="text-sm font-semibold text-white mb-3">Search Users</div>

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, account id, first name, last name…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-100 text-sm"
          />
        </div>

        {usersErr ? (
          <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
            {usersErr}
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-xl border border-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-950/40 text-gray-300">
              <tr className="text-left">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Account ID</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">KYC</th>
                <th className="px-4 py-3 text-right">Select</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {usersLoading ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    No results.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const active = selectedUserId === u.id;
                  return (
                    <tr key={u.id} className="hover:bg-gray-950/20">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-white">{u.email}</div>
                        <div className="text-xs text-gray-500">{u.id}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-200">{u.account_id}</td>
                      <td className="px-4 py-4">
                        <Pill>{String(u.role || "USER").toUpperCase()}</Pill>
                      </td>
                      <td className="px-4 py-4">
                        <Pill>{String(u.kyc_status || "UNVERIFIED").toUpperCase()}</Pill>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedUser(u)}
                          className={`px-3 py-2 rounded-lg text-xs border ${
                            active
                              ? "bg-indigo-600/20 border-indigo-500/30 text-indigo-200"
                              : "bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700"
                          }`}
                        >
                          {active ? "Selected" : "Select"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Balances for selected user */}
      <div className="mt-6 bg-gray-900 border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-900 grid place-items-center text-white">
              <Wallet size={20} />
            </div>
            <div>
              <div className="text-lg font-semibold text-white">Selected User Wallet</div>
              <div className="text-sm text-gray-400">
                {selectedUser
                  ? `${selectedUser.email} • ${selectedUser.account_id}`
                  : "Select a user to view balances."}
              </div>
            </div>
          </div>

          {selectedUserId ? (
            <button
              onClick={() => refetchBalances()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
              type="button"
            >
              <RefreshCcw size={16} />
              Refresh Balances
            </button>
          ) : null}
        </div>

        {balancesErr ? (
          <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
            {balancesErr}
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-xl border border-gray-800">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-950/40 text-gray-300">
              <tr className="text-left">
                <th className="px-4 py-3">Asset</th>
                <th className="px-4 py-3">Network</th>
                <th className="px-4 py-3">Available</th>
                <th className="px-4 py-3">Locked</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {!selectedUserId ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    Select a user above to view balances.
                  </td>
                </tr>
              ) : balancesLoading ? (
                <tr>
                  <td className="px-4 py-6 text-gray-400" colSpan={5}>
                    Loading balances…
                  </td>
                </tr>
              ) : (
                balances.map((b) => (
                  <tr key={`${b.asset}|${b.network}`} className="hover:bg-gray-950/20">
                    <td className="px-4 py-4 font-semibold text-white">{b.asset}</td>
                    <td className="px-4 py-4 text-gray-200">{b.network}</td>
                    <td className="px-4 py-4 text-gray-200">
                      {formatNum(b.available, b.asset === "BTC" ? 6 : 2)}
                    </td>
                    <td className="px-4 py-4 text-gray-200">
                      {formatNum(b.locked, b.asset === "BTC" ? 6 : 2)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openAdjust(b)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-200 text-xs"
                        disabled={!selectedUserId}
                      >
                        <ArrowUpDown size={16} />
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          Adjustments update the internal ledger only; DFNS treasury balances remain unchanged.
        </div>
      </div>

      {/* Adjust modal */}
      <Modal
        title={
          selectedUser
            ? `Adjust Balance • ${pair.asset} / ${pair.network} • ${selectedUser.email}`
            : "Adjust Balance"
        }
        open={adjustOpen}
        onClose={() => {
          if (adjustMut.isPending) return;
          setAdjustOpen(false);
        }}
      >
        <form onSubmit={submitAdjust} className="space-y-4">
          <div className="text-sm text-gray-300">
            Provide deltas (positive or negative). Backend will prevent balances going negative.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Delta Available</label>
              <input
                value={deltaAvail}
                onChange={(e) => setDeltaAvail(e.target.value)}
                placeholder="e.g. 100 or -50"
                className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Delta Locked</label>
              <input
                value={deltaLocked}
                onChange={(e) => setDeltaLocked(e.target.value)}
                placeholder="e.g. 0 or 25"
                className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Reason</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required (e.g. manual credit, correction, promo, chargeback)"
              className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
            />
          </div>

          {adjustErr ? (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {adjustErr}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={adjustMut.isPending}
            className="w-full rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 disabled:opacity-60 transition font-semibold py-2 text-white"
          >
            {adjustMut.isPending ? "Applying…" : "Apply Adjustment"}
          </button>

          <div className="text-xs text-gray-500">
            Asset/network are fixed for this row: <span className="text-gray-300">{pair.label}</span>.
          </div>
        </form>
      </Modal>
    </div>
  );
}